import React, { useEffect, useMemo, useState } from 'react'
import { Avatar } from 'antd'
import { leagueAttendanceStore, AttendeeRecord } from 'state/league/leagueAttendance'
import {
  useGetLeagueParticipants,
  LeagueParticipant,
  useLeagueDirectCheckOut,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { convertToUtc, getNameSignature } from 'helper/utility'
import { failureMessage } from 'api/global'
import Spinner from 'components/Spinner'
import ButtonSpinner from 'components/global/ButtonSpinner'
import {
  LeagueStatusLabelNone,
  LeagueStatusLabelCheckin,
  LeagueStatusLabelCheckout,
  LeagueStatusLabelCheckinPending,
  LeagueStatusLabelCheckoutPending,
  LeagueStatusLabelPending,
  LeagueStatusLabelDropIn,
  LeagueStatusLabelDroppedOut,
} from '../LeagueStatusLabel'

interface ParticipantWithType extends LeagueParticipant {
  participantType: 'kid' | 'guardian' | 'authorizedAdult' | 'dropInAttendee'
}

interface LeagueParticipantsProps {
  leagueId: string
  date: string
  searchQuery?: string
  statusFilter?: string[]
}

const toAttendeeRecord = (p: ParticipantWithType): AttendeeRecord => ({
  key: p._id,
  _id: p._id,
  name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—',
  organization: p.todayLeagueTeams?.[0]?.organization?.name || '—',
  team: p.todayLeagueTeams?.[0]?.team?.name
    || (p.teamType === 'home' ? p.game?.homeTeamId?.team?.name : p.game?.awayTeamId?.team?.name)
    || '—',
  division: p.todayLeagueTeams?.[0]?.division?.name || p.division?.name || '—',
  checkInDate: p.checkedInAt
    ? new Date(p.checkedInAt).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '—',
  hasSubstitute: false,
  checkInId: p.checkInId ?? null,
  checkInStatus: p.checkInStatus || 'no-status',
  checkOutStatus: p.checkOutStatus ?? null,
  familyId: p.familyId,
  participantType: p.participantType,
  dob: p.dob,
  gender: p.gender,
  dropInAttendeeId: (p as any).dropInAttendeeId,
})

const getButtonText = (p: ParticipantWithType): string => {
  const ci = p.checkInStatus
  const co = p.checkOutStatus
  if (!ci || ci === 'pending' || ci === 'no-status') return 'Check-in'
  if (ci === 'request-check-in' && (!co || co === 'pending')) return 'Approve check-in'
  if (ci === 'checked-in' && (!co || co === 'pending')) return 'Check-out'
  if (ci === 'checked-in' && co === 'checked-out') return 'Check-in'
  if (ci === 'checked-in' && co === 'request-check-out') return 'Check-out requested'
  if (ci === 'droped-in' && (!co || co === 'pending')) return 'Check-out'
  if (ci === 'droped-in' && co === 'dropped-out') return 'Check-in'
  return 'Check-in'
}

const LeagueParticipants: React.FC<LeagueParticipantsProps> = ({
  leagueId,
  date,
  searchQuery = '',
  statusFilter = [],
}) => {
  const [recentlyCheckedInOut, setRecentlyCheckedInOut] = useState<string[]>([])
  const [loaderId, setLoaderId] = useState<string>('')

  const {
    setCheckInModal,
    setCheckOutModal,
    setCheckInOutModalOpen,
    setDropInDetailModalOpen,
    setDropInMode,
    setSelectedAttendee,
    setAddDropInModalOpen,
  } = leagueAttendanceStore(store => ({
    setCheckInModal: store.setCheckInModal,
    setCheckOutModal: store.setCheckOutModal,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    setDropInDetailModalOpen: store.setDropInDetailModalOpen,
    setDropInMode: store.setDropInMode,
    setSelectedAttendee: store.setSelectedAttendee,
    setAddDropInModalOpen: store.setAddDropInModalOpen,
  }))

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetLeagueParticipants({ leagueId, date })

  const { mutate: directCheckOut } = useLeagueDirectCheckOut()

  const participants: ParticipantWithType[] = useMemo(() => {
    if (!data) return []
    const all: ParticipantWithType[] = [
      ...(data.kids || []).map(p => ({ ...p, participantType: 'kid' as const })),
      ...(data.guardians || []).map(p => ({ ...p, participantType: 'guardian' as const })),
      ...(data.authorizedAdults || []).map(p => ({ ...p, participantType: 'authorizedAdult' as const })),
      ...(data.dropinAttendees || []).map(p => ({ ...p, participantType: 'dropInAttendee' as const })),
    ]

    return all.filter(p => {
      if (searchQuery) {
        const name = `${p.firstName} ${p.lastName}`.toLowerCase()
        if (!name.includes(searchQuery.toLowerCase())) return false
      }
      if (statusFilter.length) {
        const ci = p.checkInStatus || 'no-status'
        const co = p.checkOutStatus
        let effectiveStatus = ci
        if (ci === 'checked-in' && co === 'checked-out') effectiveStatus = 'checked-out'
        if (ci === 'checked-in' && co === 'request-check-out') effectiveStatus = 'request-check-out'
        if (!statusFilter.includes(effectiveStatus)) return false
      }
      return true
    })
  }, [data, searchQuery, statusFilter])

  useEffect(() => {
    refetch()
  }, [date])

  useEffect(() => {
    setRecentlyCheckedInOut([])
  }, [date])

  const handleButtonClick = (p: ParticipantWithType) => {
    const today = convertToUtc(new Date())
    if (date > today) {
      failureMessage('Cannot perform actions for future dates')
      return
    }

    const buttonText = getButtonText(p)
    const record = toAttendeeRecord(p)
    setSelectedAttendee(record)

    const isDropIn = p.participantType === 'dropInAttendee'

    if (isDropIn && buttonText === 'Check-out') {
      setDropInMode('check-out')
      setCheckInOutModalOpen(false)
      setDropInDetailModalOpen(true)
    } else if (isDropIn && buttonText === 'Check-in') {
      setDropInMode('check-in')
      setCheckInOutModalOpen(false)
      setDropInDetailModalOpen(true)
    } else if (buttonText === 'Check-in' || buttonText === 'Approve check-in') {
      setCheckInOutModalOpen(false)
      setCheckInModal(true)
    } else if (buttonText === 'Check-out') {
      setCheckInOutModalOpen(false)
      setCheckOutModal(true)
    } else if (buttonText === 'Check-out requested') {
      if (!p.checkInId) return
      setLoaderId(p._id)
      directCheckOut(
        { checkInId: p.checkInId },
        {
          onSuccess: () => {
            setRecentlyCheckedInOut(prev => [...prev, p._id])
            setLoaderId('')
          },
          onError: () => setLoaderId(''),
        },
      )
    }
  }

  if (isLoading || isFetching) {
    return (
      <div className="flex py-40px justify-center">
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <div className="py-20px px-10px">Something went wrong</div>
  }

  if (!participants.length) {
    return <p className="py-20px px-10px">No participants found for this date</p>
  }

  return (
    <>
      {participants.map((p, index) => {
        const statusProps = {
          participant: toAttendeeRecord(p),
          leagueId,
          date,
          recentlyCheckedInOut,
          setRecentlyCheckedInOut,
        }

        const ci = p.checkInStatus
        const co = p.checkOutStatus

        const renderStatusLabel = () => {
          if (!ci || ci === 'no-status' || ci === 'pending') {
            return <LeagueStatusLabelNone {...statusProps} />
          }
          if (ci === 'checked-in' && (!co || co === 'pending')) {
            return <LeagueStatusLabelCheckin {...statusProps} />
          }
          if (ci === 'checked-in' && co === 'checked-out') {
            return <LeagueStatusLabelCheckout {...statusProps} />
          }
          if (ci === 'request-check-in' && (!co || co === 'pending')) {
            return <LeagueStatusLabelCheckinPending {...statusProps} />
          }
          if (ci === 'checked-in' && co === 'request-check-out') {
            return <LeagueStatusLabelCheckoutPending {...statusProps} />
          }
          if (ci === 'droped-in' && co === 'dropped-out') {
            return <LeagueStatusLabelDroppedOut />
          }
          if (ci === 'droped-in') {
            return <LeagueStatusLabelDropIn />
          }
          return <LeagueStatusLabelPending />
        }

        const buttonText = getButtonText(p)
        const isCheckIn =
          buttonText === 'Check-in' || buttonText === 'Approve check-in'

        return (
          <div
            key={p._id}
            className={`py-15px grid grid-33 participants ${
              index === participants.length - 1 ? '' : 'border-btm'
            }`}
          >
            {/* Col 1: Avatar + Name */}
            <div className="flex-center">
              <div className="w-40px">
                <Avatar size={40}>
                  {getNameSignature(`${p.firstName} ${p.lastName}`)}
                </Avatar>
              </div>
              <h3 className="text-14 w-500 ml-10">
                {p.firstName} {p.lastName}
              </h3>
            </div>

            {/* Col 2: Status Badge (Flow A — direct click) */}
            <div className="ml-40px">{renderStatusLabel()}</div>

            {/* Col 3: Action Button (Flow B — opens detail modal) */}
            <div className="ml-20px flex justify-end">
              {buttonText && <button
                className={`btn-borderless w-500 ${
                  isCheckIn ? 'text-prim' : 'danger-txt'
                }`}
                onClick={() => handleButtonClick(p)}
              >
                {loaderId === p._id ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  buttonText
                )}
              </button>}
            </div>
          </div>
        )
      })}
    </>
  )
}

export default LeagueParticipants
